import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ABSTRACT_IMG, LOGO_URL } from "../data/firmContent";
import useSEO from "../lib/useSEO";

export default function Login() {
    useSEO({ title: "Sign in", description: "Counsel portal for Rotimi Jacobs & Co — sign in to access webmail and internal tools.", path: "/login" });
    const { loginJwt, register } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
    const [busy, setBusy] = useState(false);

    const from = location.state?.from || "/webmail";

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const user = mode === "login"
                ? await loginJwt(form.email, form.password)
                : await register(form.email, form.name, form.password, form.phone);
            toast.success(`Welcome${user?.name ? `, ${user.name}` : ""}.`);
            navigate(user.role === "admin" ? "/admin" : from);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Authentication failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[80vh] grid grid-cols-1 md:grid-cols-2" data-testid="page-login">
            <div className="hidden md:block relative">
                <img src={ABSTRACT_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[#0F172A]/85" />
                <div className="relative h-full flex flex-col justify-between p-12 text-[#faf9f6]">
                    <img src={LOGO_URL} alt="RJ & Co" className="w-16 h-16 bg-white rounded-sm" />
                    <div>
                        <div className="font-mono-cap text-xs text-[#c5a059] mb-4">Counsel Portal</div>
                        <h1 className="font-serif-display text-4xl lg:text-5xl leading-tight max-w-md text-white">
                            Secure access to the chamber's <span className="text-[#c5a059]">internal systems.</span>
                        </h1>
                        <p className="mt-4 text-[#faf9f6]/85 max-w-md text-[15px]">
                            Webmail, matter notes and document review — restricted to authorised counsel.
                        </p>
                    </div>
                    <div className="font-mono-cap text-[10px] text-[#faf9f6]/40">Rotimi Jacobs & Co · Est. 1997</div>
                </div>
            </div>

            <div className="flex items-center justify-center p-8 lg:p-16 bg-[#faf9f6]">
                <div className="w-full max-w-md">
                    <div className="font-mono-cap text-xs text-[#c5a059] mb-3">
                        {mode === "login" ? "Sign in" : "Open Account"}
                    </div>
                    <h2 className="font-serif-display text-3xl md:text-4xl mb-2 text-slate-900">
                        {mode === "login" ? "Welcome back." : "Open an account."}
                    </h2>
                    <p className="text-[15px] text-slate-700 mb-8">
                        {mode === "login"
                            ? "Sign in to your client or counsel account."
                            : "Register to track orders, save publications and contact counsel."}
                    </p>

                    <form onSubmit={submit} className="space-y-4">
                        {mode === "register" && (
                            <>
                                <Field label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="login-name" />
                                <Field label="Phone number" type="tel" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="login-phone" placeholder="e.g. 0803 830 1221" />
                            </>
                        )}
                        <Field label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="login-email" />
                        <Field label="Password" type="password" required value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="login-password" />
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="login-submit-button"
                            className="w-full bg-[#0F172A] text-[#faf9f6] py-3 rounded-sm font-medium hover:bg-[#c5a059] hover:text-[#0F172A] transition-colors disabled:opacity-50"
                        >
                            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
                        </button>
                    </form>

                    <button
                        type="button"
                        onClick={() => setMode(mode === "login" ? "register" : "login")}
                        className="mt-6 text-[15px] text-slate-800 hover:text-[#c5a059]"
                        data-testid="login-toggle-mode"
                    >
                        {mode === "login" ? "New here? Open an account →" : "Already have an account? Sign in →"}
                    </button>

                    <div className="mt-8 text-xs text-slate-500">
                        <Link to="/" className="rj-link">← Back to home</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, value, onChange, required, type = "text", testid, placeholder }) => (
    <label className="block">
        <span className="font-mono-cap text-[11px] text-slate-700 block mb-2">{label}{required && " *"}</span>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            placeholder={placeholder}
            data-testid={testid}
            className="w-full px-4 py-3 border border-slate-900/20 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00a3e0] text-slate-900"
        />
    </label>
);
